/**
 * Maps API error category IDs to the display labels used in the
 * "Most common reasons for carts not being accepted" chart on the
 * Unaccepted Carts report (Looker).
 *
 * Source: looker/views/ordering_errors.view.lkml (Category dimension)
 * Source: thanx-merchant-api-new/app/api/api/v1/ordering_error_categories.rb
 *
 * Methodology: Each API category has a `pattern` field (regex) and
 * `example_messages`. We check whether those example messages would
 * match one of Looker's CASE WHEN branches (which use SQL LIKE).
 * If they match, we record the Looker category label. If they fall
 * through to ELSE, the Looker chart shows the raw message text —
 * we record that raw text instead.
 *
 * Multiple API categories can map to the same Looker report category
 * (e.g., all payment-related errors → "Payment declined/failed").
 *
 * Categories not in this map are either:
 *   - Excluded from the unaccepted carts report entirely
 *     (rewards, coupons, phone, scheduling, empty cart errors)
 *   - Not clearly mappable to a single Looker row
 *
 * MAINTENANCE: This is a manually-maintained mapping. If Looker's
 * category labels change (in ordering_errors.view.lkml) or new API
 * categories are added, this file must be updated to match. This is
 * an accepted cost — there is no runtime sync between these systems.
 */

// ── Olo categories ──────────────────────────────────────────────────────────

export const OLO_MAP: Record<string, string> = {
  // Example: 'Error validating basket'
  // Looker: exact match → 'Error validating basket (generic POS/Olo communication error)'
  error_validating_basket:
    'Error validating basket (generic POS/Olo communication error)',

  // Examples: 'This location is not currently available to take orders.', 'Online ordering is unavailable'
  // Looker: '%Online ordering is unavailable%' and '%location is not currently available%'
  online_ordering_offline: 'Online ordering offline',

  // Examples: 'This location is closed for business at the time requested.', 'Online ordering will be unavailable from...'
  // Looker: '%is closed for business at the time%' and '%Online ordering will be unavailable from%'
  location_closed_at_time: 'Location closed for business at time of order',

  // Example: 'This location is disabled.'
  // Looker: ELSE → raw message
  location_disabled: 'This location is disabled.',

  // Examples: 'Due to high order volume...', 'ASAP slots full'
  // Looker: '%high order volume%' and '%ASAP slots full%'
  high_order_volume: 'High order volume (order throttling)',

  // Example: 'Item is out of stock. Please remove it from your cart.'
  // Looker: '%is out of stock. Please remove it from your cart.%'
  item_out_of_stock: 'Items unavailable',

  // Example: 'The selected item is no longer available.'
  // Looker: '%is no longer available%'
  item_no_longer_available: 'Items unavailable',

  // Example: 'This item is not available at the specified time.'
  // Looker: '%is not available at the specified time%'
  item_not_available_at_time: 'Items unavailable',

  // Example: 'This item is not currently available.'
  // Looker: '%is not currently available%' AND NOT '%Location%'
  item_not_currently_available: 'Items unavailable',

  // Examples: 'We are currently unable to take your order...', 'DECLINED', 'AUTH. DECLINED'
  // Looker: '%DECLINED%' and '%AUTH. DECLINED%'
  payment_declined: 'Payment declined/failed',

  // Example: 'Invalid tender. Please enter a valid CVV code'
  // Looker: ELSE → raw message (each variant is its own row)
  invalid_tender_cvv: 'Payment declined/failed',

  // Example: 'Invalid tender. Credit card has expired'
  // Looker: ELSE → raw message
  invalid_tender_expired: 'Payment declined/failed',

  // Example: 'Invalid tender'
  // Looker: ELSE → raw message
  invalid_tender: 'Payment declined/failed',

  // Example: 'Your credit card has expired...'
  // Looker: ELSE → raw message
  credit_card_expired: 'Payment declined/failed',

  // Example: 'Please choose a different card and try again.'
  // Looker: '%Please choose a different card and try again.%'
  choose_different_card: 'Payment declined/failed',

  // Example: 'Please verify that the required address fields are entered.'
  // Looker: ELSE → raw message
  address_fields_missing:
    'Please verify that the required address fields are entered.',

  // Example: 'Only orders above $20.00 can be delivered...'
  // Looker: ELSE → raw message (contains variable, each instance unique)
  order_below_delivery_minimum: 'Delivery area / minimum issue',

  // Example: 'does not deliver to the selected delivery address'
  // Looker: ELSE → raw message (varies per merchant name)
  address_outside_delivery_range: 'Delivery area / minimum issue',

  // Example: "We're sorry. The gift card number you entered is either invalid..."
  // Looker: ELSE → raw message
  gift_card_invalid: 'Gift card invalid or restricted',
}

// ── Toast categories ────────────────────────────────────────────────────────

export const TOAST_MAP: Record<string, string> = {
  // Example: 'This location is disabled.'
  // Looker: ELSE → raw message
  location_disabled: 'This location is disabled.',

  // Example: 'is out of stock|is no longer available'
  // Looker: '%is out of stock%' and '%is no longer available%'
  items_unavailable: 'Items unavailable',

  // Examples: 'TRANS DENIED', 'DECLINED'
  // Looker: '%TRANS DENIED%' and '%DECLINED%'
  payment_declined: 'Payment declined/failed',

  // Example: 'SECURITY DENIAL'
  // Looker: '%SECURITY DENIAL%'
  security_denial: 'Payment declined/failed',

  // Example: 'POLICY DENIAL'
  // Looker: '%POLICY DENIAL%'
  policy_denial: 'Payment declined/failed',

  // Example: 'Insufficient funds for full amount'
  // Looker: '%Insufficient funds for full amount%'
  insufficient_funds: 'Payment declined/failed',

  // Example: 'Failed to process credit card'
  // Looker: ELSE → raw message
  failed_to_process_credit_card: 'Payment declined/failed',
}

/**
 * Provider-keyed lookup — use REPORT_CATEGORY_MAPS[providerKey] to get
 * the correct map for the merchant's ordering provider, avoiding key
 * collisions between providers.
 */
export const REPORT_CATEGORY_MAPS: Record<string, Record<string, string>> = {
  olo: OLO_MAP,
  toast: TOAST_MAP,
}
